import './WIDGT_AIND_QUANT_01_WidgetLayout.css';

import ObjectiveTypeIcon from '../../../../b2b-plans/components/objectives/ObjectiveTypeIcon';
import Status from '../../../../b2b-plans/components/Status';
import Trend from '../../../../b2b-plans/components/Trend';
import { calculateTimeProgress, getIconWithTooltip } from '../../../../b2b-plans/components/helper';
import { generateClassName, toLocaleDateTime, truncateText } from '../../../../b2b-common/helpers';
import { AdsClick, DateRange } from '@mui/icons-material';
import { CircularProgress } from '@mui/material';
import { CronData } from '../../../../b2b-plans/components/CronExpressionField';
import { WeightRatingField } from '../../../../b2b-plans/components/area-indicators/WeightRating';
import WidgetQuantitativeChart from './charting/WidgetQuantitativeChart';
import { useNavigate } from 'react-router-dom';
import { getObjectiveType } from '../../../../b2b-plans/components/objectives/QuantitativeObjectiveEditForm';
import useB2BTranslation from '../../../../b2b-common/hooks/useB2BTranslation';

const WidgetSampleValue = ({ objectiveData, sampleMeasurementUnit, cronExpression = null }) => {
    const lastSampleDate = objectiveData.lastSample.sampleDate;
    const missedExpectedSampleDate = objectiveData.missedExpectedSampleDate;
    return (
        <div className='widget-sample-value-container'>
            <CronData cronExpression={cronExpression} />
            <div className='widget-sample-value'>
                <div className='widget-sample-value-number'>
                    {objectiveData.lastSample.quantitativeValue}
                </div>
                <div className='widget-sample-measurement-unit'>
                    {sampleMeasurementUnit}
                </div>
            </div>
            <ObjectiveTypeIcon objective={objectiveData} showFieldName={false} />
            <div className='widget-sample-date'>
                {toLocaleDateTime(lastSampleDate, false)}
            </div>
            <div className='widget-sample-expected-date'>
                {objectiveData.missedExpectedSampleDate && (toLocaleDateTime(missedExpectedSampleDate, false))}
            </div>
        </div>
    );
};

const AreaIndicatorName = ({ areaIndicatorData, atPublicDashboard = false }) => {
    const navigate = useNavigate();
    const goToAreaIndicatorPath = `/plans/plans-explorer/${areaIndicatorData.area.plan.planUUID}?selectedAreaId=${areaIndicatorData.area.areaId}&selectedAreaIndicatorId=${areaIndicatorData.areaIndicatorId}`;

    const handleGoToAreaIndicator = () => {
        if (!atPublicDashboard) {
            navigate(goToAreaIndicatorPath);
        }
    };

    return (
        <div className='area-indicator-name' onClick={handleGoToAreaIndicator}>
            {truncateText(areaIndicatorData.areaIndicatorName, 7)}
        </div>
    );
};

const WidgetObjectiveInfo = ({ objectiveData, areaIndicatorData }) => {
    const objectiveType = getObjectiveType(objectiveData);
    const { t } = useB2BTranslation('b2b-reporting');
    return (
        <div className='area-indicator-objective-column-details-values'>
            <WidgetLabelValue label={t('Start date:')} value={toLocaleDateTime(objectiveData.startDate, false)} />
            <WidgetLabelValue label={t('End date:')} value={toLocaleDateTime(objectiveData.endDate, false)} />
            {objectiveType === 'Regular' && (
                <>
                    <WidgetLabelValue label={t('Start value:')} value={`${objectiveData.startQuantitativeValue} ${areaIndicatorData.indicatorDTO.quantitativeMeasurementUnitDTO.quantitativeMeasurementUnit}`} />
                    <WidgetLabelValue label={t('End value:')} value={`${objectiveData.endQuantitativeValue} ${areaIndicatorData.indicatorDTO.quantitativeMeasurementUnitDTO.quantitativeMeasurementUnit}`} />
                </>
            )}
            {objectiveType === 'Range' && (
                <>
                    <WidgetLabelValue label={t('Lower Range:')} value={`${objectiveData.startQuantitativeValue} ${areaIndicatorData.indicatorDTO.quantitativeMeasurementUnitDTO.quantitativeMeasurementUnit}`} />
                    <WidgetLabelValue label={t('Higher Range:')} value={`${objectiveData.endQuantitativeValue} ${areaIndicatorData.indicatorDTO.quantitativeMeasurementUnitDTO.quantitativeMeasurementUnit}`} />
                </>
            )}
            {(objectiveType === 'Threshold_Above' || objectiveType === 'Threshold_Below') && (
                <WidgetLabelValue label={t('Threshold value:')} value={`${objectiveData.startQuantitativeValue} ${areaIndicatorData.indicatorDTO.quantitativeMeasurementUnitDTO.quantitativeMeasurementUnit}`} />
            )}
        </div>
    );
};

const WidgetLabelValue = ({ label, value }) => {
    return (
        <div className='label-value-container'>
            <div className='label-value-label'>
                {label}
            </div>
            <div className='label-value-value'>
                {value}
            </div>
        </div>
    );
};

const WidgetStautsTrend = ({ status, trend }) => {
    return (
        <div className='widget-status-trend'>
            <Status status={status} />
            <Trend trend={trend} />
        </div>
    );
};

const WidgetCircularProgress = ({ icon, value, tooltip }) => {
    return (
        <div className='widget-circular-progress-container'>
            <div className='widget-circular-progress-chart'>
                <CircularProgress variant="determinate" value={value} thickness={5} size={35} className='widget-circular-progress-gauge' />
                <div className='widget-circular-progress-icon'>
                    {getIconWithTooltip(icon, {}, tooltip)}
                </div>
            </div>
            <div className='widget-circular-progress-value'>
                {value}
                <div className='widget-circular-progress-value-suffix'>
                    %
                </div>
            </div>
        </div>
    );
}

const WidgetTimeTargetProgress = ({ targetProgress, timeProgress }) => {
    const { t } = useB2BTranslation('b2b-reporting');
    return (
        <div className='widget-time-target-progress'>
            <WidgetCircularProgress icon={AdsClick} value={targetProgress} tooltip={t('Target Progress')} />
            <WidgetCircularProgress icon={DateRange} value={timeProgress} tooltip={t('Calendar Progress')} />
        </div>
    );
};

const WIDGT_AIND_QUANT_01_WidgetLayout = ({ areaIndicatorData, objectiveData, loading = false,
    atPublicDashboard = false, className = null }) => {
    const { t } = useB2BTranslation('b2b-reporting');

    if (loading) {
        return (
            <div className='area-indicator-objective-type'>
                {t('Loading...')}
            </div>
        );
    };

    return (
        areaIndicatorData && objectiveData && (
            <div className={generateClassName('area-indicator-objective-type', '', className)}>
                <div className='area-indicator-objective-column-data'>
                    <WidgetSampleValue
                        objectiveData={objectiveData}
                        sampleMeasurementUnit={areaIndicatorData.indicatorDTO.quantitativeMeasurementUnitDTO.quantitativeMeasurementUnit}
                        cronExpression={areaIndicatorData.samplingPeriodCronExpression}
                    />
                    <WidgetQuantitativeChart objective={objectiveData} />
                </div>
                <div className='area-indicator-objective-column-details'>
                    <AreaIndicatorName areaIndicatorData={areaIndicatorData} atPublicDashboard={atPublicDashboard} />
                    <WidgetObjectiveInfo objectiveData={objectiveData} areaIndicatorData={areaIndicatorData} />
                </div>
                <div className='area-indicator-objective-column-status'>
                    <WeightRatingField weight={areaIndicatorData.areaIndicatorWeight} />
                    <WidgetStautsTrend status={areaIndicatorData.status} trend={areaIndicatorData.trend} />
                    <WidgetTimeTargetProgress
                        targetProgress={objectiveData.progress}
                        timeProgress={calculateTimeProgress(objectiveData.startDate, objectiveData.endDate)}
                    />
                </div>
            </div>
        )
    );
}

export default WIDGT_AIND_QUANT_01_WidgetLayout;